import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { overlayDismiss } from '../utils/modalDismiss';
import { formatWindow } from '../utils/attendance';

/**
 * Copy candidates into this exam session from another exam session (e.g. the
 * Day 1 exam) or from a training day's attendance (everyone who scanned in).
 * Each gets a new access code; anyone already invited here is skipped.
 */
const CopyCandidatesModal = ({ sessionId, sessions, existingEmails, onClose, onCopied }) => {
  const [from, setFrom] = useState('session');
  const [sourceId, setSourceId] = useState('');
  const [attendanceDays, setAttendanceDays] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [people, setPeople] = useState([]);   // { id, name, email, completed? }
  const [selected, setSelected] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    Promise.all([api.get('/attendance'), api.get('/attendance/trainings')])
      .then(([d, t]) => { setAttendanceDays(d.data); setTrainings(t.data); })
      .catch(() => {});
  }, []);

  const already = (p) => existingEmails.has(String(p.email || '').trim().toLowerCase());

  // Load the chosen source's people and tick everyone who can be copied.
  useEffect(() => {
    setPeople([]);
    setSelected([]);
    if (!sourceId) return;
    setLoading(true);
    const request = from === 'session'
      ? api.get(`/sessions/${sourceId}`).then(r => r.data.invites.map(i => ({
          id: i.id, name: i.candidate_name, email: i.candidate_email,
          completed: i.status === 'completed'
        })))
      : api.get(`/attendance/${sourceId}`).then(r => r.data.records.map(x => ({
          id: x.id, name: x.name, email: x.email
        })));
    request
      .then(list => {
        setPeople(list);
        setSelected(list.filter(p => p.email && !already(p)).map(p => p.id));
      })
      .catch(() => alert('Failed to load that list'))
      .finally(() => setLoading(false));
  }, [from, sourceId]);

  const visible = people.filter(p =>
    filter === 'all' || (filter === 'completed' ? p.completed : !p.completed));
  const selectable = visible.filter(p => p.email && !already(p));
  const allTicked = selectable.length > 0 && selectable.every(p => selected.includes(p.id));

  const toggle = (pid) =>
    setSelected(s => (s.includes(pid) ? s.filter(x => x !== pid) : [...s, pid]));
  const toggleAll = () => {
    const ids = selectable.map(p => p.id);
    setSelected(s => (allTicked ? s.filter(x => !ids.includes(x)) : [...new Set([...s, ...ids])]));
  };

  // Only what is visible under the current filter is copied.
  const toCopy = selected.filter(pid => visible.some(p => p.id === pid));

  const copy = async () => {
    setCopying(true);
    try {
      const { data } = await api.post(`/sessions/${sessionId}/invites/copy`, { from, source_id: sourceId, ids: toCopy });
      const lines = [`Added ${data.created} candidate(s) with new access codes.`];
      if (data.skipped.length) {
        lines.push('', `Skipped ${data.skipped.length}:`);
        data.skipped.slice(0, 10).forEach(s => lines.push(`  ${s.name}: ${s.reason}`));
      }
      if (data.created) lines.push('', 'Remember to email them their new codes.');
      alert(lines.join('\n'));
      onCopied();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to copy candidates');
      setCopying(false);
    }
  };

  const trainingTitle = (d) => trainings.find(t => t.id === d.training_id)?.title;

  return (
    <div className="modal-overlay" {...overlayDismiss(onClose)}>
      <div className="modal" style={{ maxWidth: '640px' }}>
        <div className="modal-header">
          <h3>Copy Candidates</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="flex gap-2 mb-3">
            {[['session', 'From another exam session'], ['attendance', 'From attendance']].map(([key, label]) => (
              <button key={key} type="button"
                className={`btn btn-sm ${from === key ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => { setFrom(key); setSourceId(''); setFilter('all'); }}>
                {label}
              </button>
            ))}
          </div>

          <div className="form-group">
            <label>{from === 'session' ? 'Exam session' : 'Training day'}</label>
            <select value={sourceId} onChange={e => setSourceId(e.target.value)}>
              <option value="">— Choose —</option>
              {from === 'session'
                ? sessions.filter(s => String(s.id) !== String(sessionId)).map(s => (
                    <option key={s.id} value={s.id}>{s.session_name} ({s.invite_count || 0} candidates)</option>
                  ))
                : attendanceDays.map(d => (
                    <option key={d.id} value={d.id}>
                      {trainingTitle(d) ? `${trainingTitle(d)} › ` : ''}{d.title} · {formatWindow(d.starts_at, d.ends_at)} ({d.present_count} present)
                    </option>
                  ))}
            </select>
          </div>

          {from === 'session' && sourceId && (
            <div className="flex gap-2 mb-3">
              {[['all', 'All'], ['completed', 'Completed the exam'], ['not', 'Did not complete']].map(([key, label]) => (
                <button key={key} type="button"
                  className={`btn btn-sm ${filter === key ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setFilter(key)}>{label}</button>
              ))}
            </div>
          )}

          {loading && <p className="text-muted">Loading...</p>}
          {!loading && sourceId && visible.length === 0 && <p className="text-muted">Nobody to copy here.</p>}
          {!loading && visible.length > 0 && (
            <div style={{ maxHeight: '320px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
              <table className="table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th style={{ width: '36px' }}>
                      <input type="checkbox" checked={allTicked} onChange={toggleAll} disabled={selectable.length === 0} />
                    </th>
                    <th>Name</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(p => {
                    const note = !p.email ? 'no email' : already(p) ? 'already in this session' : null;
                    return (
                      <tr key={p.id} style={{ opacity: note ? 0.55 : 1 }}>
                        <td>
                          <input type="checkbox" checked={selected.includes(p.id)} disabled={!!note}
                            onChange={() => toggle(p.id)} />
                        </td>
                        <td>{p.name}</td>
                        <td className="text-sm">
                          {p.email || '—'}
                          {note && <span className="text-muted"> · {note}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-muted text-sm mt-3">
            Each copied candidate gets a new access code for this session. Their old code does not work here,
            so email them afterwards.
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={copy} disabled={copying || toCopy.length === 0}>
            {copying ? 'Copying...' : `Copy ${toCopy.length} candidate${toCopy.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CopyCandidatesModal;
